import { useMemo, useState } from "react";
import { Calculator, Percent } from "lucide-react";
import { Reveal } from "./Reveal";

const VARIANTS = [
  { id: "ONE", tier: "Premium", price: 1274150 },
  { id: "PRO", tier: "Performance", price: 1699150 },
  { id: "X", tier: "Adventure", price: 2124150 },
  { id: "LUXE", tier: "Luxury", price: 2549150 },
];

const ISHUSTAR_VARIANTS = [{ id: "STD", tier: "Hypercar", price: 25524000 }];

const SUV_VARIANTS = [
  { id: "EXPLORER", tier: "Essential 4x4", price: 5000000 },
  { id: "ADVENTURE", tier: "Off-Road Pack", price: 5500000 },
  { id: "SIGNATURE", tier: "Full Luxury", price: 6000000 },
];

const MODELS = [
  { id: "AERO D", tag: "Crossover", variants: VARIANTS },
  { id: "SUV", tag: "4x4 AWD Diesel", variants: SUV_VARIANTS },
  { id: "Electric", tag: "100% Electric", variants: VARIANTS },
  { id: "Ishustar", tag: "Hypercar", variants: ISHUSTAR_VARIANTS },
];

const TENURES = [36, 48, 60, 72];

const inr = (n) => "₹" + Math.round(n).toLocaleString("en-IN");

export default function EmiCalculator() {
  const [modelIdx, setModelIdx] = useState(0);
  const [variantIdx, setVariantIdx] = useState(0);
  const [downPct, setDownPct] = useState(20);
  const [months, setMonths] = useState(60);
  const [rate, setRate] = useState(9.5);

  const model = MODELS[modelIdx];
  const variants = model.variants;
  const variant = variants[Math.min(variantIdx, variants.length - 1)];
  const down = Math.round((variant.price * downPct) / 100);
  const loan = variant.price - down;
  const r = rate / 12 / 100;

  const emi = useMemo(() => {
    if (loan <= 0 || months <= 0) return 0;
    if (r === 0) return loan / months;
    const f = Math.pow(1 + r, months);
    return (loan * r * f) / (f - 1);
  }, [loan, months, r]);

  const interest = emi * months - loan;
  const total = down + emi * months;

  const chip = (active) =>
    `rounded-full border px-5 py-2 font-mono2 text-[11px] uppercase tracking-[0.15em] transition-all duration-200 ${
      active
        ? "border-[#e6b800] bg-[#e6b800] font-semibold text-[#070709]"
        : "border-white/15 text-neutral-400 hover:border-[#e6b800]/50 hover:text-white"
    }`;

  return (
    <section className="overflow-hidden border-y border-white/10 bg-[#0a0a0e] py-24 lg:py-32" data-testid="emi-calculator">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl" testId="emi-header">
          <p className="eyebrow">EMI Calculator</p>
          <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-4xl">
            Own it, one EMI at a time
          </h2>
          <p className="mt-4 text-base leading-relaxed text-neutral-400 sm:text-lg">
            Pick a variant, set your down payment and tenure — see your monthly EMI instantly.
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-[#111116] p-6 sm:p-8">
              <div>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">1 · Model</p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="emi-model-picker">
                  {MODELS.map((m, i) => (
                    <button
                      type="button"
                      key={m.id}
                      data-testid={`emi-model-${i}`}
                      onClick={() => { setModelIdx(i); setVariantIdx(0); }}
                      className={chip(modelIdx === i)}
                    >
                      {m.id} · {m.tag}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-7">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">2 · Variant</p>
                <div className="mt-3 grid grid-cols-2 gap-2.5" data-testid="emi-variant-picker">
                  {variants.map((v, i) => (
                    <button
                      type="button"
                      key={v.id}
                      data-testid={`emi-variant-${i}`}
                      onClick={() => setVariantIdx(i)}
                      className={`rounded-xl border px-4 py-3 text-left transition-all duration-200 ${
                        variantIdx === i
                          ? "border-[#e6b800] bg-[#e6b800]/10"
                          : "border-white/10 bg-[#0a0a0e] hover:border-[#e6b800]/50"
                      }`}
                    >
                      <p className="font-display text-sm font-bold text-white">{v.id}</p>
                      <p className={`mt-0.5 font-mono2 text-[10px] uppercase tracking-[0.15em] ${variantIdx === i ? "text-[#e6b800]" : "text-neutral-500"}`}>
                        {v.tier} · {inr(v.price)}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-7">
                <div className="flex items-center justify-between">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">3 · Down payment</p>
                  <p className="font-mono2 text-sm font-semibold text-[#e6b800]" data-testid="emi-downpayment-value">
                    {downPct}% · {inr(down)}
                  </p>
                </div>
                <input
                  type="range"
                  min="10"
                  max="60"
                  step="5"
                  value={downPct}
                  onChange={(e) => setDownPct(Number(e.target.value))}
                  data-testid="emi-downpayment-slider"
                  aria-label="Down payment percentage"
                  className="mt-3 w-full accent-[#e6b800]"
                />
                <div className="mt-1 flex justify-between font-mono2 text-[9px] uppercase tracking-[0.15em] text-neutral-600">
                  <span>10%</span><span>60%</span>
                </div>
              </div>

              <div className="mt-7">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">4 · Tenure</p>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="emi-tenure-picker">
                  {TENURES.map((t) => (
                    <button
                      type="button"
                      key={t}
                      data-testid={`emi-tenure-${t}`}
                      onClick={() => setMonths(t)}
                      className={chip(months === t)}
                    >
                      {t} months
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-7">
                <div className="flex items-center justify-between">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">5 · Interest rate</p>
                  <p className="flex items-center gap-1 font-mono2 text-sm font-semibold text-[#e6b800]">
                    <Percent size={12} /> {rate}% p.a.
                  </p>
                </div>
                <input
                  type="range"
                  min="7"
                  max="15"
                  step="0.5"
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  data-testid="emi-rate-slider"
                  aria-label="Interest rate"
                  className="mt-3 w-full accent-[#e6b800]"
                />
                <div className="mt-1 flex justify-between font-mono2 text-[9px] uppercase tracking-[0.15em] text-neutral-600">
                  <span>7%</span><span>15%</span>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card-glow flex h-full flex-col justify-between rounded-2xl p-7 sm:p-10" data-testid="emi-result-card">
              <div>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
                  <Calculator size={20} />
                </span>
                <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  Your monthly EMI — {model.id} {variant.id}
                </p>
                <p className="mt-2 font-display text-5xl font-extrabold text-white sm:text-6xl" data-testid="emi-monthly">
                  {inr(emi)}
                  <span className="ml-2 text-base font-semibold text-neutral-500">/month</span>
                </p>
                <p className="mt-3 text-sm text-neutral-400" data-testid="emi-monthly-with-servicing">
                  + <span className="font-semibold text-[#e6b800]">₹300 lifetime servicing</span> — कुल{" "}
                  <span className="font-semibold text-white">{inr(emi + 300)}/month</span>
                </p>
              </div>

              <div className="mt-9 space-y-3 border-t border-white/10 pt-7 text-sm">
                <div className="flex items-center justify-between" data-testid="emi-loan-amount">
                  <span className="text-neutral-400">Down payment</span>
                  <span className="font-semibold text-white">{inr(down)}</span>
                </div>
                <div className="flex items-center justify-between" data-testid="emi-loan-amount">
                  <span className="text-neutral-400">Loan amount</span>
                  <span className="font-semibold text-white">{inr(loan)}</span>
                </div>
                <div className="flex items-center justify-between" data-testid="emi-servicing-row">
                  <span className="text-neutral-400">
                    Servicing <span className="text-[#e6b800]">· lifetime ₹300</span>
                  </span>
                  <span className="font-semibold text-white">₹300/month</span>
                </div>
                <div className="flex items-center justify-between" data-testid="emi-total-interest">
                  <span className="text-neutral-400">Total interest ({months} mo)</span>
                  <span className="font-semibold text-white">{inr(interest)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-white/5 pt-3" data-testid="emi-total-payable">
                  <span className="text-neutral-400">Total payable (incl. down payment)</span>
                  <span className="font-semibold text-[#e6b800]">{inr(total)}</span>
                </div>
              </div>

              <p className="mt-7 text-[11px] leading-relaxed text-neutral-600">
                *Indicative only — 15% launch discount applied on ex-showroom price. Insurance, RTO and on-road charges are extra. Final EMI depends on your bank&apos;s approval.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}